import { useEffect } from "react";
import { Column } from "../HeadRoom";
import { initializeHeaders } from "../utils/segmentationTableHelpers";
import { useHeadroomContext } from "../context/HeadRoomProvider";

interface UseTableHeadersProps {
  columns: Column[];
}

interface TableHeaders {
  selectableHeader1: string;
  selectableHeader2: string;
  headerOptions: string[];
  setSelectableHeader1: (header: string) => void;
  setSelectableHeader2: (header: string) => void;
}

export const useTableHeaders = ({ columns }: UseTableHeadersProps): TableHeaders => {
  const {
    setCustomerColumn,
    tableName,
    selectedProject,
    count1Column,
    count2Column,
    setCount1Column,
    setCount2Column,
  } = useHeadroomContext();

  const headerOptions = columns.map((col) => `# ${col.name}`);
  const selectableHeader1 = count1Column ? `# ${count1Column}` : "";
  const selectableHeader2 = count2Column ? `# ${count2Column}` : "";

  useEffect(() => {
    const [initialHeader1, initialHeader2] = initializeHeaders(columns.map((col) => col.name));

    const columnName1 = count1Column || initialHeader1.replace(/^# /, "") || "";
    const columnName2 = count2Column || initialHeader2.replace(/^# /, "") || "";

    if (!count1Column) {
      setCount1Column(columnName1);
    }

    if (!count2Column) {
      setCount2Column(columnName2);
    }

    if (/customer/i.test(columnName1) || /prospect/i.test(columnName1)) {
      setCustomerColumn?.(columnName1);
    } else if (/customer/i.test(columnName2) || /prospect/i.test(columnName2)) {
      setCustomerColumn?.(columnName2);
    } else {
      setCustomerColumn?.("");
    }
  }, [
    columns,
    tableName,
    selectedProject,
    count1Column,
    count2Column,
    setCount1Column,
    setCount2Column,
    setCustomerColumn,
  ]);

  const handleSetHeader1 = (header: string) => {
    const columnName = header.replace(/^# /, "") || "";
    setCount1Column(columnName);

    if (/customer/i.test(columnName) || /prospect/i.test(columnName)) {
      setCustomerColumn?.(columnName);
    } else if (!/customer/i.test(count2Column) && !/prospect/i.test(count2Column)) {
      setCustomerColumn?.("");
    }
  };

  const handleSetHeader2 = (header: string) => {
    const columnName = header.replace(/^# /, "") || "";
    setCount2Column(columnName);

    if (/customer/i.test(columnName) || /prospect/i.test(columnName)) {
      setCustomerColumn?.(columnName);
    } else if (!/customer/i.test(count1Column) && !/prospect/i.test(count1Column)) {
      setCustomerColumn?.("");
    }
  };

  return {
    selectableHeader1,
    selectableHeader2,
    headerOptions,
    setSelectableHeader1: handleSetHeader1,
    setSelectableHeader2: handleSetHeader2,
  };
};
